import {
  Autocomplete,
  Button,
  IconButton,
  type Item,
  SelectInput,
  SelectInputItem,
  Tag,
  TextField,
} from '@ads/components-react';

import {
  AdjustmentsHorizontalIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, DatePicker } from '@/shared/ui/atoms';
import { cn } from '@/shared/utils/cn';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import {
  type ExamQuestionSelectionFilters,
  type SelectableQuestion,
  useExamQuestionSelection,
} from '../../../hooks/useExamQuestionSelection';
import { QuestionSelectionTable } from '../QuestionSelectionTable';

// ─── Constantes ───────────────────────────────────────────────────────────────

/**
 * Os dropdowns de Autocomplete/SelectInput são portados ao `document.body`, então
 * ficam como irmãos do `Dialog.Content` (z-50) quando este step roda dentro do
 * `ImportFromLibraryModal` — sem z-index maior eles abrem atrás do modal. Fora de
 * modal a classe é inócua. Ver reference_ads_autocomplete_selecteditem.
 */
const DROPDOWN_ABOVE_MODAL = 'z-[60]';

const LANGUAGE_TABS = [
  { value: 'pt-BR', labelKey: 'questions.selection.language.pt-BR' as const },
  { value: 'en-US', labelKey: 'questions.selection.language.en-US' as const },
  { value: 'es-ES', labelKey: 'questions.selection.language.es-ES' as const },
];

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do ExamQuestionSelectionStep */
export interface ExamQuestionSelectionStepProps {
  /** ID do ExamLang destino — filtra questões compatíveis com o tipo da prova */
  readonly examLangId: number;
  /** CultureInfos dos idiomas configurados na prova — usados para validar balanceamento */
  readonly examCultureInfos?: string[];
  /** Idioma pré-selecionado na tab ao montar o componente */
  readonly initialCultureInfo?: string;
  /** Callback disparado sempre que a seleção de questões muda (lista plana com cultureInfo em cada questão) */
  readonly onSelectionChange: (questions: SelectableQuestion[]) => void;
}

// ─── Subcomponente: Filtros ───────────────────────────────────────────────────

interface SelectionFiltersProps {
  readonly authorOptions: Item<string>[];
  readonly companyOptions: Item<string>[];
  readonly componentTypeOptions: Item<string>[];
  readonly disciplineOptions: Item<string>[];
  readonly onSearch: (filters: Partial<ExamQuestionSelectionFilters>) => void;
  readonly onClear: () => void;
  readonly onDisciplineSearchChange: (value: string) => void;
  readonly isDisciplineQuerySuccess?: boolean;
}

function SelectionFilters({
  authorOptions,
  companyOptions,
  componentTypeOptions,
  disciplineOptions,
  onSearch,
  onClear,
  onDisciplineSearchChange,
  isDisciplineQuerySuccess = false,
}: SelectionFiltersProps) {
  const { t } = useTranslation('examManagement');
  const { t: tCommon } = useTranslation();

  const [pending, setPending] = useState({
    disciplineId: null as number | null,
    personId: null as number | null,
    companyId: null as number | null,
    componentType: null as number | null,
    labelSearch: '',
    startDate: '',
    endDate: '',
  });

  const selectedDiscipline = disciplineOptions.find(
    o => o.value === (pending.disciplineId !== null ? String(pending.disciplineId) : ''),
  );
  const selectedAuthor = authorOptions.find(
    o => o.value === (pending.personId !== null ? String(pending.personId) : ''),
  );
  const selectedCompany = companyOptions.find(
    o => o.value === (pending.companyId !== null ? String(pending.companyId) : ''),
  );

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [disciplineInputValue, setDisciplineInputValue] = useState('');

  const disciplineMessage = getAutocompleteSearchMessage(
    disciplineInputValue.length,
    isDisciplineQuerySuccess,
    disciplineOptions.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  const hasFilters =
    pending.disciplineId !== null ||
    pending.personId !== null ||
    pending.companyId !== null ||
    pending.componentType !== null ||
    pending.labelSearch !== '' ||
    pending.startDate !== '' ||
    pending.endDate !== '';

  function handleSearch() {
    onSearch({ ...pending, page: 1 });
  }

  function handleClear() {
    setPending({
      disciplineId: null,
      personId: null,
      companyId: null,
      componentType: null,
      labelSearch: '',
      startDate: '',
      endDate: '',
    });
    setDisciplineInputValue('');
    onDisciplineSearchChange('');
    onClear();
  }

  return (
    <div className="rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level1">
      <div className="mb-nano">
        <TextField
          label={t('questions.selection.filters.labelSearch')}
          placeholder={t('questions.selection.filters.labelSearchPlaceholder')}
          value={pending.labelSearch}
          fullWidth
          size="md"
          onChange={e => setPending(p => ({ ...p, labelSearch: e.target.value }))}
        />
      </div>

      {showAdvanced && (
        <>
          <div className="grid grid-cols-1 gap-nano sm:grid-cols-2 lg:grid-cols-3">
            <Autocomplete<string>
              size="md"
              label={t('questions.selection.filters.discipline')}
              list={disciplineOptions}
              custom={{ optionList: DROPDOWN_ABOVE_MODAL }}
              {...(disciplineMessage !== undefined ? { notFoundMessage: disciplineMessage } : {})}
              {...(selectedDiscipline !== undefined ? { selectedItem: selectedDiscipline } : {})}
              handleChange={item => {
                setPending(p => ({
                  ...p,
                  disciplineId: item.value ? Number(item.value) : null,
                }));
                setDisciplineInputValue('');
                onDisciplineSearchChange('');
              }}
              onChange={e => {
                setDisciplineInputValue(e.target.value);
                onDisciplineSearchChange(e.target.value);
              }}
              handleClearQuery={() => {
                setDisciplineInputValue('');
                onDisciplineSearchChange('');
                setPending(p => ({ ...p, disciplineId: null }));
              }}
              placeholder={t('questions.selection.filters.disciplinePlaceholder')}
            />

            <Autocomplete<string>
              size="md"
              label={t('questions.selection.filters.author')}
              list={authorOptions}
              custom={{ optionList: DROPDOWN_ABOVE_MODAL }}
              {...(selectedAuthor !== undefined ? { selectedItem: selectedAuthor } : {})}
              handleChange={item =>
                setPending(p => ({
                  ...p,
                  personId: item.value ? Number(item.value) : null,
                }))
              }
              placeholder={t('questions.selection.filters.authorPlaceholder')}
            />

            <Autocomplete<string>
              size="md"
              label={t('questions.selection.filters.company')}
              list={companyOptions}
              custom={{ optionList: DROPDOWN_ABOVE_MODAL }}
              {...(selectedCompany !== undefined ? { selectedItem: selectedCompany } : {})}
              handleChange={item =>
                setPending(p => ({
                  ...p,
                  companyId: item.value ? Number(item.value) : null,
                }))
              }
              placeholder={t('questions.selection.filters.companyPlaceholder')}
            />
          </div>

          <div className="mt-nano grid grid-cols-1 gap-nano sm:grid-cols-2 lg:grid-cols-3">
            <DatePicker
              label={t('questions.selection.filters.startDate')}
              id="selection-filter-start-date"
              value={pending.startDate}
              onChange={iso => setPending(p => ({ ...p, startDate: iso ?? '' }))}
            />

            <DatePicker
              label={t('questions.selection.filters.endDate')}
              id="selection-filter-end-date"
              value={pending.endDate}
              onChange={iso => setPending(p => ({ ...p, endDate: iso ?? '' }))}
            />

            <SelectInput
              size="md"
              label={t('questions.selection.filters.type')}
              custom={{ content: DROPDOWN_ABOVE_MODAL }}
              fullWidth
              value={pending.componentType !== null ? String(pending.componentType) : ''}
              placeholder={t('questions.selection.filters.typePlaceholder')}
              onValueChange={value =>
                setPending(p => ({
                  ...p,
                  componentType: value ? Number(value) : null,
                }))
              }
            >
              {componentTypeOptions.map(item => (
                <SelectInputItem
                  key={item.value ?? ''}
                  value={item.value ?? ''}
                  custom={{ root: 'text-nowrap' }}
                >
                  {item.label}
                </SelectInputItem>
              ))}
            </SelectInput>
          </div>
        </>
      )}

      <div className="mt-nano flex items-center gap-nano">
        <Button
          hierarchy="secondary"
          size="md"
          leadingIcon={showAdvanced ? ChevronUpIcon : AdjustmentsHorizontalIcon}
          onClick={() => setShowAdvanced(v => !v)}
        >
          {showAdvanced
            ? t('questions.selection.filters.lessFilters')
            : t('questions.selection.filters.moreFilters')}
        </Button>

        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={MagnifyingGlassIcon}
          onClick={handleSearch}
        >
          {t('questions.selection.filters.search')}
        </Button>

        {hasFilters && (
          <IconButton
            icon={XMarkIcon}
            ariaLabel={t('questions.selection.filters.clearFilters')}
            hierarchy="tertiary"
            size="md"
            onClick={handleClear}
          />
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Step de seleção de questões da biblioteca no wizard de criação de avaliação.
 *
 * Exibe painel de filtros e tabela paginada com checkboxes para seleção de
 * questões. Notifica o pai via `onSelectionChange` a cada alteração.
 *
 * @example
 * ```tsx
 * <ExamQuestionSelectionStep
 *   examLangId={examLangId}
 *   onSelectionChange={(questions) => setSelectedQuestions(questions)}
 * />
 * ```
 */
export function ExamQuestionSelectionStep({
  examLangId,
  examCultureInfos = [],
  initialCultureInfo,
  onSelectionChange,
}: ExamQuestionSelectionStepProps) {
  const { t } = useTranslation('examManagement');

  const {
    data,
    isLoading,
    totalCount,
    currentPage,
    totalPages,
    filters,
    selectedIds,
    allSelectedQuestions,
    selectionCounts,
    isSelectionBalanced,
    authorOptions,
    companyOptions,
    componentTypeOptions,
    disciplineOptions,
    isDisciplineQuerySuccess,
    updateFilters,
    resetFilters,
    toggleQuestion,
    setDisciplineSearch,
  } = useExamQuestionSelection(examLangId, examCultureInfos, initialCultureInfo);

  const authorItems: Item<string>[] = authorOptions.map(o => ({
    label: o.value ?? '',
    value: String(o.key ?? ''),
  }));
  const companyItems: Item<string>[] = companyOptions.map(o => ({
    label: o.value ?? '',
    value: String(o.key ?? ''),
  }));
  const componentTypeItems: Item<string>[] = componentTypeOptions.map(o => ({
    label: o.value ?? '',
    value: String(o.key ?? ''),
  }));
  const disciplineItems: Item<string>[] = disciplineOptions.map(o => ({
    label: o.value ?? '',
    value: String(o.key ?? ''),
  }));

  // Notifica o pai com a lista plana de todas as questões selecionadas (todos os idiomas)
  useEffect(() => {
    onSelectionChange(allSelectedQuestions);
  }, [allSelectedQuestions, onSelectionChange]);

  function handleToggle(question: SelectableQuestion) {
    toggleQuestion(question);
  }

  const totalSelected = allSelectedQuestions.length;
  const hasImbalance = !isSelectionBalanced && totalSelected > 0 && examCultureInfos.length > 1;

  return (
    <div className="flex flex-col gap-mini">
      <div className="flex items-center justify-between">
        <h3 className="ts-subtitle-xs text-ctx-content-title">{t('questions.selection.title')}</h3>
        {totalSelected > 0 && (
          <Tag
            label={t('questions.selection.selectedCount', {
              count: totalSelected,
            })}
            status="highlight"
            size="sm"
            custom={{ text: 'text-nowrap' }}
          />
        )}
      </div>

      {/* Tabs de idioma */}
      <div className="flex gap-xs w-full">
        {LANGUAGE_TABS.map(tab => {
          const count = selectionCounts[tab.value] ?? 0;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => updateFilters({ cultureInfo: tab.value, page: 1 })}
              className={cn(
                'flex flex-1 items-center justify-center gap-nano bg-ctx-layout-spotlight hover:bg-ctx-interactive-secondaryHover text-ctx-content-base ts-paragraph-xxs rounded-sm !border-hairline border-ctx-layout-border p-mini shadow-level1',
                filters.cultureInfo === tab.value
                  ? 'border-ctx-content-base bg-ctx-layout-body text-ctx-content-title'
                  : '',
              )}
            >
              {t(tab.labelKey)}
              {count > 0 && (
                <span
                  className={cn(
                    'rounded-pill px-quark ts-caption-xxs',
                    filters.cultureInfo === tab.value
                      ? 'bg-ctx-feedback-brand-default text-base-neutral-white'
                      : 'bg-ctx-layout-border text-ctx-content-base',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Aviso de desbalanceamento */}
      {hasImbalance && (
        <Alert type="warning">
          {t('questions.selection.balanceWarning', {
            counts: examCultureInfos
              .map(lang => {
                const tab = LANGUAGE_TABS.find(l => l.value === lang);
                const label = tab ? t(tab.labelKey) : lang;
                return `${label}: ${selectionCounts[lang] ?? 0}`;
              })
              .join(' · '),
          })}
        </Alert>
      )}

      <SelectionFilters
        authorOptions={authorItems}
        companyOptions={companyItems}
        componentTypeOptions={componentTypeItems}
        disciplineOptions={disciplineItems}
        onSearch={updateFilters}
        onClear={resetFilters}
        onDisciplineSearchChange={setDisciplineSearch}
        isDisciplineQuerySuccess={isDisciplineQuerySuccess}
      />

      <QuestionSelectionTable
        data={data}
        totalCount={totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onPageChange={page => updateFilters({ page })}
      />
    </div>
  );
}
